import React, { useState } from "react";
import { motion } from "framer-motion";
import { Bot, Code, Zap } from "lucide-react";

const HeroSection = () => {
  const [showContactModal, setShowContactModal] = useState(false);

  // Services data for the hero section
  //   const services = [
  //     {
  //       id: "ai-agents",
  //       title: "AI Agents",
  //       icon: (
  //         <svg
  //           className="w-8 h-8"
  //           viewBox="0 0 24 24"
  //           fill="none"
  //           xmlns="http://www.w3.org/2000/svg"
  //         >
  //           <path
  //             d="M12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16Z"
  //             stroke="currentColor"
  //             strokeWidth="2"
  //             strokeLinecap="round"
  //             strokeLinejoin="round"
  //           />
  //           <path
  //             d="M3 12H4M12 3V4M20 12H21M12 20V21M18.364 18.364L17.657 17.657M18.364 5.636L17.657 6.343M5.636 5.636L6.343 6.343M5.636 18.364L6.343 17.657"
  //             stroke="currentColor"
  //             strokeWidth="2"
  //             strokeLinecap="round"
  //             strokeLinejoin="round"
  //           />
  //         </svg>
  //       ),
  //       description:
  //         "Intelligent assistants that interact naturally with users while autonomously handling complex tasks",
  //       features: [
  //         "Conversational AI for natural interaction",
  //         "Domain-specific knowledge bases",
  //       ],
  //     },
  //     {
  //       id: "automation",
  //       title: "Business Automation",
  //       icon: (
  //         <svg
  //           className="w-8 h-8"
  //           viewBox="0 0 24 24"
  //           fill="none"
  //           xmlns="http://www.w3.org/2000/svg"
  //         >
  //           <path
  //             d="M16 8H8M16 8C18.2091 8 20 9.79086 20 12C20 14.2091 18.2091 16 16 16H8M16 8V6M8 16C5.79086 16 4 14.2091 4 12C4 9.79086 5.79086 8 8 8M8 16V18"
  //             stroke="currentColor"
  //             strokeWidth="2"
  //             strokeLinecap="round"
  //             strokeLinejoin="round"
  //           />
  //         </svg>
  //       ),
  //       description:
  //         "Streamlined workflows and optimized processes for real estate and recruitment businesses",
  //       features: ["Custom workflow automation", "Data processing pipelines"],
  //     },
  //   ];
  const services = [
    {
      id: "digital-solutions",
      title: "Digital Solutions",
      icon: (
        <svg
          className="w-8 h-8"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9 17H15M9 17C7.89543 17 7 16.1046 7 15V9C7 7.89543 7.89543 7 9 7H15C16.1046 7 17 7.89543 17 9V15C17 16.1046 16.1046 17 15 17M9 17L5 21M15 17L19 21M12 3V7"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      description:
        "Crafting responsive web applications and intuitive mobile experiences that connect businesses with their customers",
      features: ["Responsive web applications", "Cross-platform mobile apps"],
    },
    {
      id: "intelligent-systems",
      title: "Intelligent Systems",
      icon: (
        <svg
          className="w-8 h-8"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="2 4"
          />
        </svg>
      ),
      description:
        "Harnessing AI and automation to transform business operations with conversational agents and smart workflows",
      features: ["Conversational AI assistants", "Custom workflow automation"],
    },
  ];

  return (
    <section
      id="hero"
      className="min-h-screen pt-20 relative flex items-center justify-center overflow-hidden bg-black text-white"
    >
      <div className="relative w-full md:w-[99%] lg:w-[99%] mx-auto bg-transparent flex items-center justify-center overflow-hidden min-h-screen">
        {/* Enhanced Background Elements */}
        <div className="absolute inset-0 flex items-center justify-center opacity-5">
          <div className="w-64 h-64 md:w-96 md:h-96 lg:w-full lg:h-full max-w-3xl max-h-3xl bg-gradient-to-r from-blue-800 to-purple-800 rounded-full blur-3xl animate-pulse" />
        </div>

        {/* Animated grid background - with visible dots */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #444444 1px, transparent 0)",
            backgroundSize: "40px 40px",
          }}
        ></div>

        {/* Animated floating elements */}
        <div className="absolute inset-0 overflow-hidden opacity-10 pointer-events-none">
          {/* Code fragments */}
          <div className="absolute top-[15%] left-[10%] text-xs md:text-sm font-mono text-blue-600 animate-float-slow">
            {`async function automate() {`}
          </div>
          <div className="absolute top-[20%] left-[12%] text-xs md:text-sm font-mono text-blue-800 animate-float-slower">
            {`  await ai.process(data);`}
          </div>
          <div className="absolute top-[25%] left-[14%] text-xs md:text-sm font-mono text-blue-600 animate-float-slow">
            {`  return optimized;`}
          </div>
          <div className="absolute top-[30%] left-[12%] text-xs md:text-sm font-mono text-blue-800 animate-float-slower">
            {`}`}
          </div>

          {/* Workflow arrows */}
          <div className="absolute top-[60%] right-[15%] rotate-45 animate-pulse-slow">
            <svg
              width="80"
              height="80"
              viewBox="0 0 24 24"
              className="text-blue-200 opacity-50"
            >
              <path
                d="M7 7h10v10"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.5"
              />
              <path
                d="M7 17L17 7"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.5"
              />
            </svg>
          </div>

          {/* Connection dots */}
          <div className="absolute h-40 w-40 top-[20%] right-[20%]">
            <div className="relative h-full w-full">
              <div className="absolute h-1 w-1 rounded-full bg-blue-400 animate-ping-slow"></div>
              <div className="absolute h-1 w-1 rounded-full bg-blue-400 top-[30%] left-[80%] animate-ping-slower"></div>
              <div className="absolute h-1 w-1 rounded-full bg-blue-400 top-[70%] left-[40%] animate-ping-slow"></div>
              <div className="absolute h-1 w-1 rounded-full bg-blue-400 top-[90%] left-[90%] animate-ping-slower"></div>
              <svg
                width="100%"
                height="100%"
                className="absolute inset-0 text-blue-200 opacity-30"
              >
                <line
                  x1="0%"
                  y1="0%"
                  x2="80%"
                  y2="30%"
                  stroke="currentColor"
                  strokeWidth="0.5"
                />
                <line
                  x1="0%"
                  y1="0%"
                  x2="40%"
                  y2="70%"
                  stroke="currentColor"
                  strokeWidth="0.5"
                />
                <line
                  x1="0%"
                  y1="0%"
                  x2="90%"
                  y2="90%"
                  stroke="currentColor"
                  strokeWidth="0.5"
                />
                <line
                  x1="80%"
                  y1="30%"
                  x2="40%"
                  y2="70%"
                  stroke="currentColor"
                  strokeWidth="0.5"
                />
                <line
                  x1="80%"
                  y1="30%"
                  x2="90%"
                  y2="90%"
                  stroke="currentColor"
                  strokeWidth="0.5"
                />
                <line
                  x1="40%"
                  y1="70%"
                  x2="90%"
                  y2="90%"
                  stroke="currentColor"
                  strokeWidth="0.5"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-6">
          <div className="max-w-5xl mx-auto text-center">
            <div className="relative inline-block">
              <h1 className="font-mono text-4xl md:text-6xl font-bold mb-4 tracking-tight">
                TRESOR KL
                <span className="text-blue-600 animate-blink">_</span>
              </h1>
              <div className="absolute -top-8 -right-8 text-blue-100 opacity-30 animate-spin-slow hidden md:block">
                <svg
                  width="60"
                  height="60"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="0.5"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v8M8 12h8" />
                </svg>
              </div>
            </div>

            <p className="font-mono pt-4 text-gray-400 mb-8 relative">
              <span className="relative inline-flex items-center">
                AI
                <span className="mx-2 inline-flex h-1.5 w-1.5 rounded-full bg-gray-500"></span>
              </span>
              <span className="relative inline-flex items-center">
                Automation
                <span className="mx-2 inline-flex h-1.5 w-1.5 rounded-full bg-gray-500"></span>
              </span>
              <span className="relative inline-flex items-center">
                Development
              </span>
            </p>

            <p className="mx-auto max-w-3xl text-lg md:text-xl mb-10 text-gray-300">
              Building intelligent solutions at the intersection of AI and
              software development. Specialized in creating conversational AI
              agents, custom automation workflows, and full-stack web & mobile
              applications for businesses across South Africa.
            </p>

            <div className="grid pt-12 grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
              {services.map((service, index) => (
                <div
                  key={service.id}
                  className="bg-white border-gray-800 p-6 rounded-xl border flex flex-col items-center text-left transition-all hover:shadow-md group"
                >
                  <div className="relative">
                    <div className="w-8 h-8 text-blue-600 mb-4 relative z-10 group-hover:scale-110 transition-transform">
                      {service.icon}
                    </div>
                    <div className="absolute -inset-2 bg-blue-900/20 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-0 animate-pulse"></div>
                  </div>
                  <h3 className="font-mono text-gray-700 font-bold text-lg mb-2">
                    {service.title}
                  </h3>
                  <p className="font-mono text-sm text-gray-500">
                    {service.description}
                  </p>
                  <div className="mt-4 w-full border-t border-gray-800 pt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex justify-between items-center text-xs text-gray-400">
                      <div className="flex items-center gap-1">
                        <Code className="w-3 h-3" />
                        <span>{service.features[0]}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Zap className="w-3 h-3" />
                        <span>{service.features[1]}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-6 justify-center mt-2">
              <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
                <a href="#services" className="group relative overflow-hidden">
                  <div className="bg-white text-black hover:bg-gray-200 px-6 py-3 rounded-lg font-mono transition-colors flex items-center justify-center gap-2">
                    <span className="absolute inset-0 w-0 bg-blue-600 transition-all duration-500 ease-out group-hover:w-full"></span>
                    <svg
                      className="w-4 h-4 relative z-10"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11 4H4V20H11"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M16 16L20 12L16 8"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M20 12H8"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span className="relative z-10">Explore Services</span>
                    <div className="absolute right-2 opacity-0 group-hover:opacity-100 transition-opacity relative z-10">
                      <svg
                        className="w-4 h-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5 12H19M19 12L12 5M19 12L12 19"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Ping effect behind button */}
                  <span className="absolute -inset-0.5 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 opacity-0 group-hover:opacity-30 group-hover:blur-sm transition-all duration-500 -z-10"></span>
                </a>

                <div className="hidden md:flex items-center text-gray-400 text-xs">
                  <span className="mx-3">or</span>
                </div>

                <div className="flex items-center pl-1 lg:pl-4 ml-1 lg:ml-3 border-l border-gray-800">
                  <button
                    onClick={() => setShowContactModal(true)}
                    className="px-5 py-4 bg-blue-600 hover:bg-black text-white text-sm font-medium rounded-md transition-all duration-300 flex items-center gap-2 relative overflow-hidden group"
                  >
                    <span className="absolute inset-0 w-0 bg-black transition-all duration-500 ease-out group-hover:w-full"></span>
                    <Bot className="w-4 h-4 relative z-10" />
                    <span className="relative z-10">Let's Talk</span>
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-center mt-2 opacity-80">
                <div className="flex items-center space-x-1 text-xs text-gray-400">
                  <svg
                    className="w-4 h-4 text-gray-400"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 12H15"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 9L12 15"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12Z"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                  <span>
                    Building AI-powered solutions that deliver measurable
                    results
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
